import { FC, useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui';
import { useToast } from '@/contexts/ToastContext';
import { createCheckoutSession, getPlans } from '@/services/subscription.service';
import { getErrorMessage } from '@/utils/error';
import { Plan } from '@/types/payment';

interface Step8PaymentProps {
  onBack?: () => void;
  onComplete: () => void;
  hasPendingPayment?: boolean;
}

export const Step8Payment: FC<Step8PaymentProps> = ({ onBack, hasPendingPayment }) => {
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [manualPaymentSuccess, setManualPaymentSuccess] = useState(hasPendingPayment || false);
  const { addToast } = useToast();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoadingPlans(true);
        const data = await getPlans();
        // Sort by sortOrder
        const sorted = data.filter(p => p.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
        setPlans(sorted);

        // Auto-select SXM or first plan
        const sxm = sorted.find(p => p.name === 'SXM' || p.name === 'SXM+MXM');
        if (sxm) setSelectedPlan(sxm.id);
        else if (sorted.length > 0) setSelectedPlan(sorted[0].id);

      } catch (err: unknown) {
        console.error('Failed to load plans:', err);
        addToast('Failed to load subscription plans.', 'error');
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, [addToast]);

  const handleCheckout = async () => {
    try {
      setLoading(true);
      if (!selectedPlan) {
        addToast('Please select a plan to continue.', 'error');
        return;
      }

      const data = await createCheckoutSession(selectedPlan);


      if (data?.manualMode) {
        // Handle manual payment success
        setManualPaymentSuccess(true);
        addToast(data.message || 'Invoice sent successfully', 'success');
        return;
      }

      if (data?.url) {
        addToast('Redirecting to checkout...', 'success');
        // Small delay to show the toast before redirect
        const redirectUrl = data.url;
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 500);
        return;
      }

      throw new Error('Invalid response from server. Please try again.');

    } catch (error: unknown) {
      console.error('Error creating checkout session:', error);
      addToast(getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  if (manualPaymentSuccess) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-6">
          <Check className="w-8 h-8" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Invoice Sent!</h2>
        <p className="text-slate-600 max-w-md mx-auto mb-8">
          We have sent an invoice to your email address. Please follow the instructions in the email to complete your payment manually.
        </p>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 max-w-md w-full mb-8">
          <h3 className="font-semibold text-slate-900 mb-2">What happens next?</h3>
          <ul className="text-sm text-slate-600 space-y-3 text-left">
            <li className="flex gap-2">
              <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600 flex-shrink-0">1</div>
              <span>Check your email for payment details.</span>
            </li>
            <li className="flex gap-2">
              <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600 flex-shrink-0">2</div>
              <span>Complete the transfer as instructed.</span>
            </li>
            <li className="flex gap-2">
              <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600 flex-shrink-0">3</div>
              <span>Reply to the email with your proof of payment.</span>
            </li>
            <li className="flex gap-2">
              <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600 flex-shrink-0">4</div>
              <span>Our team will verify and activate your account.</span>
            </li>
          </ul>
        </div>

        <p className="text-sm text-slate-500">
          Once verified, you will receive a confirmation email and full access to the portal.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Choose Your Plan</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          Select a subscription plan to complete your setup
        </p>
      </div>

      {/* Plans */}
      {/* Plans */}
      {loadingPlans ? (
        <div className="col-span-1 md:col-span-2 flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {plans.map((plan) => (
            <div
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={`relative px-4 py-5 border rounded-lg cursor-pointer transition-all ${selectedPlan === plan.id
                ? 'border-slate-900 bg-slate-50'
                : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
            >
              {/* Popular badge logic - for now specific to SXM+MXM or if it's the only plan */}
              {(plan.name === 'SXM+MXM' || plans.length === 1) && (
                <div className="absolute -top-2.5 left-4">
                  <span className="px-2.5 py-0.5 bg-slate-900 text-white text-xs font-medium rounded-full">
                    Popular
                  </span>
                </div>
              )}

              {/* Radio */}
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  checked={selectedPlan === plan.id}
                  onChange={() => setSelectedPlan(plan.id)}
                  className="mt-0.5 w-4 h-4 text-slate-900 border-slate-300 focus:ring-slate-900/10"
                />
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-slate-900">{plan.displayName || plan.name}</h3>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-2xl font-bold text-slate-900">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: plan.currency || 'USD' }).format(plan.price)}
                    </span>
                    <span className="text-sm text-slate-500">/{plan.interval}</span>
                  </div>
                </div>
              </div>

              {/* Features */}
              {plan.features && plan.features.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {plan.features.map((feature: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
        <p className="text-xs text-blue-900">
          <strong>Note:</strong> Payment is required to complete setup and access the Carion Portal.
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-6 mt-6 border-t border-slate-200">
        {onBack && (
          <Button type="button" variant="secondary" onClick={onBack}>
            Back
          </Button>
        )}
        <Button
          type="button"
          variant="primary"
          fullWidth
          onClick={handleCheckout}
          isLoading={loading}
        >
          Proceed to Checkout
        </Button>
      </div>
    </div>
  );
};
